import * as React from "react";
import { Page, Card, Table, Alert, Text, Button, Grid, Form } from "tabler-react";
import TradesSiteWrapper from "./TradesSiteWrapper.react";
import RefreshButton from "./RefreshButton.react";
import { getDecisions, todayIST, fmtDateTime } from "./api";

class DecisionsPage extends React.Component {
  state = {
    loading: true,
    error: "",
    data: null,
    dateFrom: "",
    dateTo: "",
    phase: "",
  };

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const today = todayIST();
    this.setState(
      {
        dateFrom: params.get("date_from") || today,
        dateTo: params.get("date_to") || today,
        phase: params.get("phase") || "",
      },
      this.load
    );
  }

  load = async () => {
    const { dateFrom, dateTo, phase } = this.state;
    this.setState({ loading: true, error: "" });
    try {
      const query = { n: 2000 };
      if (dateFrom) query.date_from = dateFrom;
      if (dateTo) query.date_to = dateTo;
      if (phase) query.phase = phase;
      const resp = await getDecisions(query);
      this.setState({ data: resp, loading: false });
    } catch (e) {
      this.setState({ error: e.message || "Failed to load decisions", loading: false });
    }
  };

  setToday = () => {
    const today = todayIST();
    this.setState({ dateFrom: today, dateTo: today }, this.load);
  };

  clearDates = () => {
    this.setState({ dateFrom: "", dateTo: "" }, this.load);
  };

  renderConditions(r) {
    const conds = r.conditions || [];
    if (conds.length === 0) {
      return r.condition_detail || r.primary_blocker || "-";
    }
    return (
      <div className="small">
        {conds.map((c, i) => (
          <div key={`${c.id}-${i}`} className={c.ok ? "text-success" : "text-danger"}>
            {c.ok ? "✓" : "✗"} {c.id}
            {c.detail ? <span className="text-muted"> — {c.detail}</span> : null}
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { loading, error, data, dateFrom, dateTo, phase } = this.state;
    const rows = (data && data.entries) || [];
    return (
      <TradesSiteWrapper>
        <Page.Content
          title="Strategy Decisions"
          subTitle="Why a trade was taken or skipped — strategy, symbol, and condition match."
          options={<RefreshButton onClick={this.load} loading={loading} />}
        >
          <Alert type="info">
            <span data-page-note="1">
              This is why a trade was taken or skipped. Filter the date and phase when a trade is missing or looks wrong.
            </span>
          </Alert>
          {error ? <Alert type="danger">{error}</Alert> : null}
          <Card>
            <Card.Body>
              <Grid.Row>
                <Grid.Col md={3}>
                  <Form.Group label="From date">
                    <Form.Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => this.setState({ dateFrom: e.target.value })}
                    />
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={3}>
                  <Form.Group label="To date">
                    <Form.Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => this.setState({ dateTo: e.target.value })}
                    />
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={2}>
                  <Form.Group label="Phase">
                    <Form.Select
                      value={phase}
                      onChange={(e) => this.setState({ phase: e.target.value })}
                    >
                      <option value="">All phases</option>
                      <option value="buy">buy</option>
                      <option value="sell">sell</option>
                      <option value="shadow_entry">shadow_entry</option>
                      <option value="shadow_exit">shadow_exit</option>
                      <option value="skip">skip</option>
                      <option value="reconcile">reconcile</option>
                    </Form.Select>
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={4}>
                  <Form.Group label={"\u00a0"}>
                    <div className="d-flex" style={{ gap: 8, flexWrap: "wrap" }}>
                      <Button color="primary" onClick={this.load} disabled={loading}>
                        Apply filter
                      </Button>
                      <Button color="secondary" outline onClick={this.setToday} disabled={loading}>
                        Today
                      </Button>
                      <Button color="secondary" outline onClick={this.clearDates} disabled={loading}>
                        All
                      </Button>
                    </div>
                  </Form.Group>
                </Grid.Col>
              </Grid.Row>
            </Card.Body>
          </Card>
          {loading ? <Text>Loading decisions…</Text> : null}
          {!loading && !error ? (
            <React.Fragment>
              <Text className="text-muted mb-3">
                {(data && data.date_filter_caption) || ""}{" "}
                Showing <strong>{rows.length}</strong> entries.
              </Text>
              {rows.length === 0 ? (
                <Alert type="info">No decision rows for this filter.</Alert>
              ) : (
                <Card>
                  <Card.Header>
                    <Card.Title>{rows.length} decisions</Card.Title>
                  </Card.Header>
                  <Table responsive highlightRowOnHover>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColHeader>Time</Table.ColHeader>
                        <Table.ColHeader>Strategy</Table.ColHeader>
                        <Table.ColHeader>Phase</Table.ColHeader>
                        <Table.ColHeader>Outcome</Table.ColHeader>
                        <Table.ColHeader>Trade</Table.ColHeader>
                        <Table.ColHeader>Conditions</Table.ColHeader>
                        <Table.ColHeader>Summary</Table.ColHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rows.map((r, i) => (
                        <Table.Row key={`${r.ts}-${i}`}>
                          <Table.Col>{fmtDateTime(r.ts)}</Table.Col>
                          <Table.Col>
                            {r.strategy_id || "-"}
                            {r.index_key ? (
                              <div className="text-muted small">{r.index_key}</div>
                            ) : null}
                          </Table.Col>
                          <Table.Col>{r.phase}</Table.Col>
                          <Table.Col
                            className={
                              r.outcome === "success"
                                ? "text-success"
                                : r.outcome === "error"
                                  ? "text-danger"
                                  : ""
                            }
                          >
                            {r.outcome}
                          </Table.Col>
                          <Table.Col>
                            {r.symbol || r.leg || "-"}
                            {r.leg && r.symbol ? (
                              <div className="text-muted small">{r.leg}</div>
                            ) : null}
                            {r.qty !== "" && r.qty != null ? (
                              <div className="text-muted small">qty {r.qty}</div>
                            ) : null}
                          </Table.Col>
                          <Table.Col>{this.renderConditions(r)}</Table.Col>
                          <Table.Col>{r.summary}</Table.Col>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </Card>
              )}
            </React.Fragment>
          ) : null}
        </Page.Content>
      </TradesSiteWrapper>
    );
  }
}

export default DecisionsPage;
